import axios from 'axios';
import alert_mixin from '@/utils/mixins/alert_mixin';
import {
  BIND_TEMPLATES_ENTITY_CONFIG,
  GET_FULL_CONFIG_BY_ENTITY_TYPE_URL,
  get_request_config,
  LIST_TEMPLATE_VIEW_URL, UNASSIGN_TEMPLATE_BINDING
} from '@/utils/system_constants/constant';

export default {
  name: 'BindTemplates',
  mixins: [alert_mixin],
  props: {
    entityTypeId: {
      type: Number,
      required: false,
      default: null,
    },
    entityTypeName: {
      type: String,
      required: false,
      default: '',
    },
    entityConfigId: {
      type: Number,
      required: false,
      default: null,
    },
  },
 data: () => ({
      loading: false,
      load_error: null,
      templates: [],
      template_bindings: [],
      selected_template_ids: [],
      template_dropdown_open: false,
      creating_template_binding: false,
      unassign_binding_id: null,
  }),
  computed: {
    bindable_templates() {
      const bound_template_ids = new Set(
        (this.template_bindings || [])
          .map((binding) => Number(binding?.template_id ?? binding?.template))
          .filter((id) => Number.isFinite(id))
      );

      return (Array.isArray(this.templates) ? this.templates : [])
        .filter((template) => Number.isFinite(Number(template?.id)))
        .filter((template) => !bound_template_ids.has(Number(template.id)));
    },
    available_templates_count() {
      return Array.isArray(this.templates) ? this.templates.length : 0;
    },
    bound_templates_count() {
      return Array.isArray(this.template_bindings) ? this.template_bindings.length : 0;
    },
    selected_templates_count() {
      return Array.isArray(this.selected_template_ids) ? this.selected_template_ids.length : 0;
    },
    selected_templates_label() {
      if (this.loading) return 'Loading templates...';
      if (!this.bindable_templates.length) return 'No templates available';
      if (!this.selected_templates_count) return 'Select template(s) to bind';
      return `${this.selected_templates_count} template${this.selected_templates_count === 1 ? '' : 's'} selected`;
    },
  },
  mounted() {
    this.fetch_templates();
    this.fetch_template_bindings();
    document.addEventListener('click', this.on_document_click, true);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.on_document_click, true);
  },
  methods: {
    on_document_click(event) {
      if (!this.template_dropdown_open) return;
      const root = this.$refs?.templateMultiselect;
      if (!root || root.contains(event.target)) return;
      this.template_dropdown_open = false;
    },
    toggle_template_dropdown() {
      if (this.loading || this.creating_template_binding) return;
      this.template_dropdown_open = !this.template_dropdown_open;
    },
    is_template_binding_like(item) {
      return item && typeof item === 'object' && (
        'template_id' in item ||
        'template' in item ||
        'template_document_entity_type' in item ||
        'document_name' in item ||
        'document_type' in item
      );
    },

    get_template_id_from_binding(binding_record) {
      return (
        binding_record?.template_id ??
        binding_record?.template ??
        binding_record?.template_document_entity_type ??
        null
      );
    },

    build_template_record(template_record) {
      const result = {
        id: template_record.id,
        name: template_record.name || template_record.template_name,
        description: template_record.description || template_record.template_description,
        type: template_record.template_type || template_record.type,
        url: template_record.document_url || template_record.presigned_url,
      };

      return result;
    },

    build_binding_record(binding_record, matched_template, template_id) {
      const result = {
        ...binding_record,
        template_id: template_id != null ? Number(template_id) : null,
        name: matched_template?.name || binding_record?.document_name || binding_record?.name || 'Template',
        description: matched_template?.description || binding_record?.description || '',
        type: matched_template?.type || binding_record?.document_type || 'template',
        url:
          matched_template?.url ||
          binding_record?.url ||
          binding_record?.document_url ||
          binding_record?.presigned_url ||
          '',
      };
      return result;
    },

    get_active_template_bindings(config_data) {
      const array_candidates = Object.values(config_data).filter(Array.isArray);
      const binding_array = array_candidates.find((binding_list) =>
        binding_list.some((item) => this.is_template_binding_like(item))
      );
      const template_binding_records = Array.isArray(binding_array) ? binding_array : [];
      return template_binding_records.filter((binding) => binding?.is_active !== false);
    },

    parse_response_data(payload) {
      if (typeof payload !== 'string') return payload;

      const trimmed = payload.trim();
      if (!trimmed) return {};

      let result;

      try {
        result = JSON.parse(trimmed);
      } catch (error) {
        result = {
          status: 'error',
          message: error?.message || 'Invalid JSON response received from the server.'
        };
      }

      return result;
    },

    async fetch_template_bindings() {
      const entity_type_id = Number(this.entityTypeId);
      if (!Number.isFinite(entity_type_id) || entity_type_id <= 0) {
        this.template_bindings = [];
        return;
      }

      return axios
        .get(
          GET_FULL_CONFIG_BY_ENTITY_TYPE_URL,
          get_request_config({ params: { entity_type_id } })
        )
        .then((response) => {
          const parsed_response = this.parse_response_data(response.data);
          if (parsed_response?.status && parsed_response.status !== 'success') {
            this.template_bindings = [];
            return;
          }

          const config_data = parsed_response?.data || {};
          const active_bindings = this.get_active_template_bindings(config_data);

          this.template_bindings = active_bindings
            .map((binding_record) => {
              const template_id = this.get_template_id_from_binding(binding_record);
              const matched_template = (Array.isArray(this.templates) ? this.templates : [])
                .find((template_record) => String(template_record?.id) === String(template_id));

              return this.build_binding_record(binding_record, matched_template, template_id);
            })
            .filter((binding_record, binding_index, binding_list) =>
              binding_list.findIndex((candidate) => String(candidate?.id) === String(binding_record?.id)) === binding_index
            );
        })
        .catch(() => {
          this.template_bindings = [];
        });
    },

    async add_template_bindings() {
      const selected_ids = Array.isArray(this.selected_template_ids)
        ? this.selected_template_ids.map((id) => String(id)).filter(Boolean)
        : [];

      if (!selected_ids.length) {
        this.show_error('Please select at least one template to bind.');
        return;
      }

      this.creating_template_binding = true;
      try {
        const entity_config_id = Number(this.entityConfigId);
        if (!Number.isFinite(entity_config_id) || entity_config_id <= 0) {
          this.show_error('Entity config is not loaded yet. Please wait and try again.');
          return;
        }

        const unique_ids = Array.from(new Set(selected_ids));
        const successes = [];
        const failures = [];

        for (const raw_id of unique_ids) {
          const template_id = Number(raw_id);
          if (!Number.isFinite(template_id) || template_id <= 0) {
            failures.push({ template_id: raw_id, message: 'Invalid template selected.' });
            continue;
          }

          try {
            const response = await axios.post(
              BIND_TEMPLATES_ENTITY_CONFIG,
              { template_id, entity_config_id },
              get_request_config()
            );
            const parsed_response = this.parse_response_data(response.data);
            if (parsed_response?.status && parsed_response.status !== 'success') {
              throw new Error(String(parsed_response?.message || 'Failed to bind template'));
            }

            const created_binding = parsed_response?.data || {};
            const matched_template = (Array.isArray(this.templates) ? this.templates : [])
              .find((template_record) => String(template_record?.id) === String(template_id));

            const binding = {
              ...created_binding,
              template_id,
              name: matched_template?.name || created_binding?.document_name || 'Template',
              description: matched_template?.description || '',
              type: matched_template?.type || created_binding?.document_type || 'template',
              url: matched_template?.url || '',
            };

            this.template_bindings = [
              ...(Array.isArray(this.template_bindings) ? this.template_bindings : [])
                .filter((existing_binding) => String(existing_binding?.id) !== String(binding?.id)),
              binding,
            ];

            successes.push(template_id);
          } catch (error) {
            failures.push({ template_id, message: error?.message || 'Failed to bind template' });
          }
        }

        if (successes.length) {
          this.show_success(`${successes.length} template${successes.length === 1 ? '' : 's'} bound successfully`);
        }

        if (failures.length) {
          this.show_error(
            failures.length === 1
              ? (failures[0]?.message || 'Failed to bind template')
              : `${failures.length} template${failures.length === 1 ? '' : 's'} failed to bind`
          );
        }

        this.selected_template_ids = failures.map((failure) => String(failure.template_id)).filter(Boolean);
        this.template_dropdown_open = false;
      } finally {
        this.creating_template_binding = false;
      }
    },
    async fetch_templates() {
      this.loading = true;
      this.load_error = null;

      try {
        const response = await axios.get(LIST_TEMPLATE_VIEW_URL, get_request_config());
        const parsed_response = this.parse_response_data(response.data);

        if (parsed_response?.status && parsed_response.status !== 'success') {
          throw new Error(String(parsed_response?.message || 'Failed to load templates'));
        }

        const template_list = Array.isArray(parsed_response?.data) ? parsed_response.data : [];
        this.templates = template_list.map((template_record) => this.build_template_record(template_record));
      } catch (error) {
        this.templates = [];
        this.load_error = error?.message || 'Failed to load templates.';
        this.show_error(this.load_error);
      } finally {
        this.loading = false;
      }
    },

    go_to_entity_overview() {
      this.$router.push({
        name: 'EntityConfigDetail',
        params: {
          entityTypeId: this.entityTypeId
        }
      });
    },

    unassign_template_binding(binding_record) {
      const binding_id = Number(binding_record?.id);
      if (!Number.isFinite(binding_id) || binding_id <= 0) {
        return;
      }

      if (this.unassign_binding_id === binding_id) return;

      this.show_confirm(
        'Unbind template?',
        'This will remove the template binding from this entity configuration.',
        () => {
          this.unassign_binding_id = binding_id;
          axios.post(
            UNASSIGN_TEMPLATE_BINDING,{ binding_id },get_request_config()
          )
          .then((response) => {
            const parsed_response = this.parse_response_data(response.data);

            if (parsed_response?.status && parsed_response.status !== 'success') {
              this.show_error(parsed_response?.message || 'Failed to unbind template');
              return;
            }

            this.template_bindings = (Array.isArray(this.template_bindings) ? this.template_bindings : [])
              .filter((existing_binding) => Number(existing_binding?.id) !== binding_id);

            this.show_success(parsed_response?.message || 'Template unbound successfully');
          })
          .catch((error) => {
            this.show_error(error?.message || 'Failed to unbind template.');
          })
          .finally(() => {
            if (this.unassign_binding_id === binding_id) {
              this.unassign_binding_id = null;
            }
          });
        }
      );
    }
  },
};
